import { z } from 'zod';
import { TRUST_TIERS } from './account-properties.types';
import { DocumentType } from 'models/jobs/jobs';
const constraintSchema = z
    .object({
    country: z.string().length(2).optional(),
    type: z.enum(DocumentType).optional()
})
    .refine((obj) => Object.keys(obj).length > 0, {
    message: 'country or type are required'
});
const livenessEnum = z.enum([
    'mouth',
    'distance',
    'orientation',
    'enhanced',
    'straight'
]);
const verificationPropertiesSchema = z.object({
    ssn: z.boolean(),
    ssnPrivate: z.boolean(),
    tinValidationEnabled: z.boolean(),
    ein: z.string(),
    ssnNameMatchThreshold: z.number().min(0).max(100),
    ssnDobStrictMatch: z.boolean(),
    cipScoringEnabled: z.boolean(),
    dobVerification: z.boolean(),
    includeMiddleName: z.boolean(),
    rejectExpiredIds: z.boolean(),
    aliasMatchEnabled: z.boolean(),
    nameFuzzyMatchEnabled: z.boolean(),
    nameBoundaryMatchEnabled: z.boolean(),
    dobFuzzyMatch: z.boolean(),
    rejectStandardLivenessSkipped: z.boolean(),
    rejectEnhancedLivenessSkipped: z.boolean(),
    liveness: livenessEnum.nullable(),
    aml: z.boolean(),
    amlIdv: z.boolean(),
    amlFuzziness: z.number().min(0).max(1),
    amlSearchProfile: z.string(),
    amlScreeningConfig: z.string(),
    amlMonitoring: z
        .object({
        enabled: z.boolean(),
        days: z.number().nullable(),
        contactEmails: z.array(z.string())
    })
        .partial(),
    vidvEnabled: z.boolean(),
    crosscheck: z.boolean(),
    dlv_enabled: z.boolean(),
    disableScandit: z.boolean(),
    reverificationEnabled: z.boolean(),
    inspectIP: z.boolean(),
    idConstraints: z
        .object({
        allow: z.array(constraintSchema).nonempty(),
        deny: z.array(constraintSchema).nonempty()
    })
        .partial(),
    smsCountryDenyList: z.array(z.string().length(2)),
    smsCountryAllowList: z.array(z.string().length(2)),
    tosOptions: z
        .object({
        consentPageEnabled: z.boolean(),
        privacyPolicyUrl: z.string(),
        biometricsPolicyUrl: z.string(),
        consentCopy: z.enum(['default', 'international', 'vouchedProvider']),
        internationalConsentEnabled: z.boolean()
    })
        .partial(),
    nist: z
        .object({
        ial2ComplianceEnabled: z.boolean(),
        ial2ProofingTemplate: z
            .object({
            type: z.enum(['default', 'customer_continue']),
            customerName: z.string()
        })
            .partial()
    })
        .partial(),
    digitalIdOptions: z
        .object({ enabled: z.boolean(), verificationProfileId: z.string() })
        .partial(),
    notifications: z
        .object({
        emails: z.array(z.string()),
        job: z.object({ completed: z.boolean() }).partial()
    })
        .partial(),
    crosscheckConfig: z
        .object({
        model: z.enum(['approval', 'fraud']).optional(),
        strictNameLinkage: z.boolean().optional()
    })
        .optional(),
    trustTier: z.enum(TRUST_TIERS).optional(),
    callbackURL: z.string(),
    nowWebApp: z
        .object({
        liveness: livenessEnum,
        type: z.string(),
        id: z.string(),
        face: z.string(),
        dob: z.string().nullable(),
        ial2CrossCheckId: z.string().nullable(),
        callbackURL: z.string().nullable(),
        crossDeviceURL: z.string(),
        enableIPAddress: z.boolean().nullable(),
        enableDriversLicenseValidation: z.boolean().nullable(),
        enablePhysicalAddress: z.boolean().nullable(),
        enableDarkWeb: z.boolean().nullable(),
        enableCrossCheck: z.boolean().nullable(),
        enableIdDistance: z.boolean(),
        enableEyeCheck: z.boolean(),
        includeBarcode: z.boolean(),
        includeBackId: z.boolean(),
        crossDevice: z.boolean(),
        crossDeviceQRCode: z.boolean(),
        crossDeviceHandoff: z.boolean(),
        crossDeviceSMS: z.boolean()
    })
        .partial(),
    now: z.object({ path: z.string() }).partial()
});
export const verificationPropertiesParsingSchema = verificationPropertiesSchema.partial();
export const FORM_KEYS = new Set([
    'ssn',
    'ssnPrivate',
    'ssnNameMatchThreshold',
    'ssnDobStrictMatch',
    'cipScoringEnabled',
    'ein',
    'tinValidationEnabled',
    'dobVerification',
    'includeMiddleName',
    'rejectExpiredIds',
    'aliasMatchEnabled',
    'nameFuzzyMatchEnabled',
    'nameBoundaryMatchEnabled',
    'dobFuzzyMatch',
    'rejectStandardLivenessSkipped',
    'rejectEnhancedLivenessSkipped',
    'liveness',
    'aml',
    'amlIdv',
    'amlFuzziness',
    'amlSearchProfile',
    'amlScreeningConfig',
    'amlMonitoring',
    'vidvEnabled',
    'crosscheck',
    'crosscheckConfig',
    'trustTier',
    'dlv_enabled',
    'disableScandit',
    'reverificationEnabled',
    'inspectIP',
    'idConstraints',
    'smsCountryDenyList',
    'smsCountryAllowList',
    'tosOptions',
    'nist',
    'digitalIdOptions',
    'notifications',
    'callbackURL',
    'nowWebApp',
    'now'
]);
/** Top-level keys written by their own uploader and hidden from the Form/JSON editors. */
export const UPLOAD_MANAGED_KEYS = new Set(['pluginImages']);
export const SCHEMA_WARNING_PREFIX = 'Config has schema violations (loaded anyway):\n';
